import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthProvider';
import { ChevronDown, User, Settings, HelpCircle, LogOut, Key } from 'lucide-react';
import ChangePasswordModal from './ChangePasswordModal';

const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownToggleRef = useRef(null);
  const dropdownContainerRef = useRef(null);
  const dropdownInstanceRef = useRef(null);

  useEffect(() => {
    const toggleEl = dropdownToggleRef.current;
    const containerEl = dropdownContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      dropdownInstanceRef.current = instance;
      const onShow = () => setIsDropdownOpen(true);
      const onHide = () => setIsDropdownOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        dropdownInstanceRef.current = null;
      };
    }
  }, []);
  
  // Get user initials for fallback
  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Load profile image
  useEffect(() => {
    if (user?.photo) {
      setImageError(false);
      setProfileImage(user.photo);
    } else {
      setProfileImage(null);
    }
  }, [user?.photo]);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/masterdata';
  };

  const handleProfileClick = () => {
    // Navigate to profile page
    navigate('/profile');
  };

  const handleSettingsClick = () => {
    // Navigate to settings page
    console.log('Navigate to settings');
  };

  const handleChangePasswordClick = () => {
    setIsChangePasswordModalOpen(true);
  };

  const handleChangePasswordSubmit = async (formData) => {
    try {
      // TODO: Implement password change API call
      console.log('Changing password:', formData);
      
      // For now, just show success message
      // You can replace this with actual API call
      // Note: Replace with @Alert component when available
      alert('Password changed successfully!');
      
      // Close modal
      setIsChangePasswordModalOpen(false);
    } catch (error) {
      console.error('Password change failed:', error);
      throw error;
    }
  };

  return (
    <div ref={dropdownContainerRef} className="nav-item dropdown">
      <a
        ref={dropdownToggleRef}
        href="#"
        className="nav-link d-flex align-items-center text-reset px-2 py-1"
        aria-label="Open user menu"
        aria-expanded={isDropdownOpen}
        onClick={(e) => {
          e.preventDefault();
          const inst = dropdownInstanceRef.current;
          if (inst?.toggle) inst.toggle();
        }}
        style={{
          borderRadius: '0.5rem',
          transition: 'all 0.2s ease-in-out',
          minHeight: '40px',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#f8f9fa';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'transparent';
        }}
        onMouseOut={(e) => {
          // Fallback to ensure hover state is cleared
          if (!e.currentTarget.contains(e.relatedTarget)) {
            e.target.style.backgroundColor = 'transparent';
          }
        }}
        onFocus={(e) => {
          e.target.style.backgroundColor = '#f8f9fa';
          e.target.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.25)';
        }}
        onBlur={(e) => {
          e.target.style.backgroundColor = 'transparent';
          e.target.style.boxShadow = 'none';
        }}
      >
        <span 
          className="avatar avatar-sm position-relative flex-shrink-0 d-flex align-items-center justify-content-center"
          style={{ 
            backgroundImage: profileImage && !imageError ? `url(${profileImage})` : 'none',
            backgroundColor: !profileImage || imageError ? '#6c757d' : 'transparent',
            border: '2px solid #e9ecef',
            transition: 'all 0.2s ease-in-out',
            color: 'white',
            fontWeight: '600',
            fontSize: '0.75rem'
          }}
        >
          {profileImage && !imageError && (
            <img 
              src={profileImage} 
              alt="Profile" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover', 
                borderRadius: '50%' 
              }}
              onError={handleImageError}
            />
          )}
          {(!profileImage || imageError) && (
            <span>{getUserInitials(user?.username || user?.name)}</span>
          )}
          <span 
            className="badge bg-success position-absolute" 
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              bottom: '2px',
              right: '2px',
              border: '2px solid white'
            }}
          ></span>
        </span>
        
        <div className="d-none d-lg-block ms-2 flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.875rem', lineHeight: '1.2' }}>
            {user?.username || 'User'}
          </div>
          <div className="mt-1">
            <span 
              className="small text-secondary" 
              style={{ 
                fontSize: '0.7rem',
                fontWeight: '400',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }}
            >
              {user?.jobTitle || '(No Job Title)'}
            </span>
          </div>
        </div>
        
        <div className="d-none d-lg-block ms-2 flex-shrink-0">
          <ChevronDown 
            className="text-muted" 
            size={16}
          />
        </div>
      </a>
      
      <div className="dropdown-menu dropdown-menu-end dropdown-menu-arrow" 
           style={{ 
             minWidth: '200px',
             boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
             border: '1px solid rgba(0, 0, 0, 0.05)',
             borderRadius: '0.5rem'
           }}>
        
        {/* Menu Items */}
        <a 
          href="#" 
          className="dropdown-item d-flex align-items-center py-2 px-3" 
          onClick={(e) => {
            e.preventDefault();
            handleProfileClick();
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          onMouseOut={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          <User className="me-3" size={20} />
          <span>Profile</span>
        </a>

        {/* <a 
          href="#" 
          className="dropdown-item d-flex align-items-center py-2 px-3" 
          onClick={(e) => {
            e.preventDefault();
            handleSettingsClick();
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          onMouseOut={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          <Settings className="me-3" size={20} />
          <span>Settings</span>
        </a> */}

        <a 
          href="#" 
          className="dropdown-item d-flex align-items-center py-2 px-3" 
          onClick={(e) => {
            e.preventDefault();
            handleChangePasswordClick();
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          onMouseOut={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          <Key className="me-3" size={20} />
          <span>Change Password</span>
        </a>

        <a 
          href="https://support.cbqaglobal.co.id/" 
          className="dropdown-item d-flex align-items-center py-2 px-3" 
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          onMouseOut={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          <HelpCircle className="me-3" size={20} />
          <span>Help & Support</span>
        </a>

        <div className="dropdown-divider my-2"></div>

        <a 
          href="#" 
          className="dropdown-item d-flex align-items-center py-2 px-3 text-danger" 
          onClick={(e) => {
            e.preventDefault();
            handleLogout();
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#fef2f2';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          onMouseOut={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          <LogOut className="me-3" size={20} />
          <span>Logout</span>
        </a>
      </div>
      
      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        onSubmit={handleChangePasswordSubmit}
      />
    </div>
  );
};

export default UserMenu; 